import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useT, T } from "@transifex/react";
import { loadPopulus } from "helpers/populus";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import { Button } from "storybook/components/button";
import { Loading } from "components/loading";
import { useAdminContent, useFeatureFlags } from "context/admin-content";
import { useRiskReport } from "context/risk-report";
import { useRiskProfile, RiskKey } from "context/risk-profile";
import { AdvertProvider } from "context/advert";
import { SectionHeading } from "./section-heading";
import { NextStepsSection } from "./sections/next-steps";
import { RiskResultsSection } from "./sections/risk-results";
import { Content } from "../styles";
import { ButtonContainer, GlobalStyle, PageHeading } from "./styles";
import { ShareSection } from "./sections/share";

type Config = {
  key: string;
  displayValue: string;
  guidanceKey: string | null;
  adminDataKey: string;
};

type VideoConfigs = Record<string, Config>;

enum RiskSections {
  testing = "testing",
  breastMri = "breastMri",
  lungCt = "lungCt",
  counseling = "counseling",
  average = "average",
}

enum Guidance {
  testingGuidance = "testingGuidance",
  mriGuidance = "mriGuidance",
  lungGuidance = "lungGuidance",
  counselingGuidance = "counselingGuidance",
}

export function PatientResultsDisplay() {
  const t = useT();
  const adminData = useAdminContent();
  const { enableBreastMRIWorkflow, enableLungCTWorkflow } = useFeatureFlags();
  const riskProfile = useRiskProfile();
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [selectedEligibility, setSelectedEligibility] = useState<
    RiskKey | undefined
  >();
  const { isLoading: reportLoading } = useRiskReport();
  const pageRef = useRef<HTMLHeadingElement | null>(null);

  const isLoading = adminData?.isLoading || reportLoading;

  const handleBackToTopClick = useCallback(() => {
    pageRef.current?.scrollIntoView();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(
        window.document.body.clientHeight / 3 <= window.scrollY
      );
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    loadPopulus();
  }, []);

  const offSiteScreeningEligibilities = useMemo(() => {
    if (!adminData || !riskProfile?.riskProfile) return [];

    const videoConfigs: VideoConfigs = {
      [RiskSections.testing]: {
        key: RiskSections.testing,
        displayValue: t("WATCH: About Genetic Testing"),
        guidanceKey: Guidance.testingGuidance,
        adminDataKey: RiskSections.testing,
      },
      [RiskSections.breastMri]: {
        key: RiskSections.breastMri,
        displayValue: t("WATCH: Breast MRI"),
        guidanceKey: Guidance.mriGuidance,
        adminDataKey: RiskSections.breastMri,
      },
      [RiskSections.lungCt]: {
        key: RiskSections.lungCt,
        displayValue: t("WATCH: Lung CT"),
        guidanceKey: Guidance.lungGuidance,
        adminDataKey: RiskSections.lungCt,
      },
      [RiskSections.counseling]: {
        key: RiskSections.counseling,
        displayValue: t("WATCH: Counseling"),
        guidanceKey: Guidance.counselingGuidance,
        adminDataKey: RiskSections.counseling,
      },
      [RiskSections.average]: {
        key: RiskSections.average,
        displayValue: t("WATCH: Average Risk"),
        guidanceKey: null, // No guidance check for average risk
        adminDataKey: RiskSections.average,
      },
    };

    const createVideoSetting = (config: Config) => ({
      key: config.key,
      displayValue: config.displayValue,
      ...adminData.patientResults[
        config.adminDataKey as keyof typeof adminData.patientResults
      ],
    });

    const isEligible = (guidanceKey: string | null) => {
      if (!guidanceKey) return true; // For average risk
      return riskProfile.riskProfile.attributes[guidanceKey]?.meets_criteria;
    };

    // Define which eligibilities to show based on feature flags
    const eligibilitiesToShow = () => {
      if (enableBreastMRIWorkflow && enableLungCTWorkflow) {
        return {
          mode: "workflow",
          eligibilities: [
            RiskSections.testing,
            RiskSections.breastMri,
            RiskSections.lungCt,
          ],
        };
      }

      if (enableBreastMRIWorkflow) {
        return {
          mode: "workflow",
          eligibilities: [RiskSections.testing, RiskSections.breastMri],
        };
      }

      if (enableLungCTWorkflow) {
        return {
          mode: "workflow",
          eligibilities: [RiskSections.testing, RiskSections.lungCt],
        };
      }

      // Default hierarchy: testing > mri > lung > counseling > average risk
      return {
        mode: "hierarchy",
        eligibilities: [
          RiskSections.testing,
          RiskSections.breastMri,
          RiskSections.lungCt,
          RiskSections.counseling,
          RiskSections.average,
        ],
      };
    };

    const { mode, eligibilities } = eligibilitiesToShow();

    // Workflow mode: show all eligible videos
    if (mode === "workflow") {
      return eligibilities
        .map((key) => videoConfigs[key])
        .filter((config) => config && isEligible(config.guidanceKey))
        .map(createVideoSetting);
    }

    // Hierarchy mode: show first eligible video
    for (const key of eligibilities) {
      const config = videoConfigs[key];
      if (config && isEligible(config.guidanceKey)) {
        return [createVideoSetting(config)];
      }
    }

    return [];
  }, [
    adminData,
    riskProfile?.riskProfile,
    enableBreastMRIWorkflow,
    enableLungCTWorkflow,
    t,
  ]);

  const eligibilitiesWithVideosEnabled = useMemo(
    () =>
      offSiteScreeningEligibilities.filter(
        (eligibility) => eligibility.videoEnabled
      ),
    [offSiteScreeningEligibilities]
  );

  const showVideos = eligibilitiesWithVideosEnabled.length > 0;

  let riskResult;
  if (showVideos) {
    riskResult =
      selectedEligibility && adminData?.patientResults[selectedEligibility];
  } else {
    riskResult = riskProfile?.patientRiskResult;
  }

  const riksProfileAttributes = riskProfile?.riskProfile?.attributes || {};
  const riskProfileValues = Object.entries(riksProfileAttributes);
  const riskProfileValuesWithMeetsCriteria = riskProfileValues.filter(
    ([key, value]: [string, any]) => {
      if (key === "mriGuidance") {
        return enableBreastMRIWorkflow && value.meets_criteria;
      }

      return value.meets_criteria;
    }
  );

  const sections = [
    {
      title: t("Risk Results"),
      content: <RiskResultsSection />,
    },
    {
      title: t("What To Do Next"),
      content: (
        <NextStepsSection
          eligibilitiesWithVideosEnabled={eligibilitiesWithVideosEnabled}
          setSelectedEligibility={setSelectedEligibility}
          riskResult={riskResult}
          riskProfileValuesWithMeetsCriteria={
            riskProfileValuesWithMeetsCriteria
          }
        />
      ),
    },
    { title: t("Share"), content: <ShareSection /> },
  ];

  if (isLoading) {
    return (
      <Content>
        <Loading />
      </Content>
    );
  }

  return (
    <AdvertProvider>
      <>
        <GlobalStyle />
        <PageHeading ref={pageRef}>Patient Results Page</PageHeading>
        {sections.map((section, index) => (
          <React.Fragment key={section.title}>
            <SectionHeading number={index + 1} title={section.title} />
            {section.content}
          </React.Fragment>
        ))}
        <ButtonContainer $show={showScrollButton}>
          <Button
            theme="default"
            variant="text"
            color="inherit"
            onClick={handleBackToTopClick}
            startIcon={<ArrowUpwardIcon color="primary" />}
          >
            <T _str="Back to top" />
          </Button>
        </ButtonContainer>
      </>
    </AdvertProvider>
  );
}
